// Data import/export functions
import { v4 as uuidv4 } from 'uuid';

// Define LocationType constant directly to avoid circular imports
const LocationType = {
  NORMAL: 'normal',
  START: 'start',
  END: 'end',
  WAYPOINT: 'waypoint'
};

// Export locations as JSON
export const exportLocationsToJson = (locations, filename = "locations.json") => {
  try {
    // Format data for future import
    const exportData = {
      locations: locations.map(loc => ({
        id: loc.id,
        name: loc.name,
        latitude: loc.latitude,
        longitude: loc.longitude,
        type: loc.type // Include point type information
      })),
      exportTime: new Date().toISOString()
    };
    
    // Convert to JSON string
    const jsonString = JSON.stringify(exportData, null, 2);
    
    // Create Blob object
    const blob = new Blob([jsonString], { type: "application/json" });
    
    // Create download link
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    
    // Add to DOM, trigger download, then remove
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    // Release URL object
    URL.revokeObjectURL(url);
    
    return true;
  } catch (error) {
    console.error('Failed to export location points data:', error);
    throw error;
  }
};

// Import locations from JSON
export const importLocationsFromJson = (file) => {
  return new Promise((resolve, reject) => {
    try {
      const reader = new FileReader();
      
      reader.onload = (event) => {
        try {
          const jsonData = JSON.parse(event.target.result);
          
          // Validate data format
          if (!jsonData.locations || !Array.isArray(jsonData.locations)) {
            throw new Error('Invalid JSON format, missing locations array');
          }
          
          // Validate each location point object
          const validatedLocations = jsonData.locations.map((loc, index) => {
            // Check required fields
            if (!loc.latitude || !loc.longitude) {
              throw new Error(`Location point ${index+1} missing required coordinate information`);
            }
            
            // Return normalized location object
            // Preserve original ID and type, use defaults if not present
            return {
              id: loc.id || uuidv4(),
              name: loc.name || `Point ${index+1}`,
              latitude: parseFloat(loc.latitude),
              longitude: parseFloat(loc.longitude),
              type: loc.type || LocationType.NORMAL // Preserve original type or use default type
            };
          });
          
          resolve(validatedLocations);
        } catch (parseError) {
          reject(parseError);
        }
      };
      
      reader.onerror = () => {
        reject(new Error('File read failed'));
      };
      
      reader.readAsText(file);
    } catch (error) {
      reject(error);
    }
  });
};

// Import from CSV - expects: Number,Type,Quantity,Address,Latitude,Longitude
export const importLocationsFromCsv = async (file) => {
  try {
    if (!file) {
      throw new Error('No file provided');
    }

    // Validate file type
    if (!file.name.endsWith('.csv') && file.type !== 'text/csv') {
      throw new Error('Please upload a CSV format file');
    }

    // Read file content
    const text = await file.text();
    const lines = text.split('\n').map(line => line.trim()).filter(line => line);
    
    if (lines.length === 0) {
      throw new Error('CSV file is empty');
    }

    // Parse CSV lines (skip header if present)
    const locations = [];
    const validationErrors = [];
    const skippedEntries = [];
    let startIndex = 0;
    
    // Check if first line is header
    const firstLine = lines[0].toLowerCase();
    if (firstLine.includes('number') && firstLine.includes('type') && firstLine.includes('latitude') && firstLine.includes('longitude')) {
      startIndex = 1;
    }

    for (let i = startIndex; i < lines.length; i++) {
      const line = lines[i];
      
      // Parse CSV line - format: Number,Type,Quantity,Address,Latitude,Longitude
      const columns = line.split(',').map(col => col.trim());
      
      // console.log(`Line ${i + 1}: "${line}" -> ${columns.length} columns:`, columns);
      
      if (columns.length < 6) {
        console.warn(`Skipping line ${i + 1}: insufficient columns (expected at least 6: Number,Type,Quantity,Address,Latitude,Longitude)`);
        continue;
      }
      
      // Extract fields
      const longitude = columns[columns.length - 1];
      const latitude = columns[columns.length - 2];
      const number = columns[0];
      const type = columns[1];
      const quantity = columns[2];
      // Address may contain commas
      const addressParts = columns.slice(3, columns.length - 2);
      const address = addressParts.join(',').trim();

      // Parse values
      const numberParsed = parseInt(number);
      const quantityParsed = parseFloat(quantity);
      const latitudeParsed = parseFloat(latitude);
      const longitudeParsed = parseFloat(longitude);

      // console.log(`Parsed values - number:${numberParsed}, type:"${type}", quantity:${quantityParsed}, address:"${address}", lat:${latitudeParsed}, lng:${longitudeParsed}`);
      
      // Validate coordinates
      if (isNaN(latitudeParsed) || isNaN(longitudeParsed)) {
        console.warn(`Skipping line ${i + 1}: invalid coordinates - lat:${latitude}(${latitudeParsed}) lng:${longitude}(${longitudeParsed})`);
        continue;
      }

      if (latitudeParsed < -90 || latitudeParsed > 90 || longitudeParsed < -180 || longitudeParsed > 180) {
        console.warn(`Skipping line ${i + 1}: coordinates out of range - lat:${latitudeParsed} lng:${longitudeParsed}`);
        continue;
      }

      // Validate quantity
      let quantityError = null;
      if (quantity === undefined || quantity === null || quantity === '') {
        quantityError = 'Quantity is required';
      } else if (isNaN(quantityParsed)) {
        quantityError = `Invalid quantity "${quantity}" - must be a number`;
      } else if (quantityParsed <= 0) {
        quantityError = `Invalid quantity ${quantityParsed} - must be positive`;
      } else if (!Number.isInteger(quantityParsed)) {
        quantityError = `Invalid quantity ${quantityParsed} - must be a whole number (no decimals)`;
      }
      
      if (quantityError) {
        const errorEntry = {
          lineNumber: i + 1,
          rawLine: lines[i],
          number: numberParsed || 'unknown',
          type: type || 'unknown',
          error: quantityError
        };
        validationErrors.push(errorEntry);
        skippedEntries.push(errorEntry);
        console.warn(`Skipping line ${i + 1}: ${quantityError} - "${quantity}"`);
        continue;
      }

      // Validate type
      const normalizedType = type.toLowerCase();
      if (!['supply', 'demand'].includes(normalizedType)) {
        console.warn(`Skipping line ${i + 1}: invalid type - "${type}"(${normalizedType}) (must be Supply or Demand)`);
        continue;
      }

      // Set location type
      let locationType = LocationType.NORMAL;
      if (normalizedType === 'supply') {
        locationType = LocationType.START; // Use START type for supply points
      } else if (normalizedType === 'demand') {
        locationType = LocationType.WAYPOINT; // Use WAYPOINT type for demand points
      }

      const locationObj = {
        id: uuidv4(),
        name: address || `${type} ${numberParsed}`,
        latitude: latitudeParsed,
        longitude: longitudeParsed,
        type: locationType,
        // TP-specific fields
        number: numberParsed,
        tpType: normalizedType,
        quantity: quantityParsed,
        address: address
      };
      
      locations.push(locationObj);
    }

    if (locations.length === 0) {
      if (validationErrors.length > 0) {
        throw new Error(`No valid location points found. All ${validationErrors.length} entries had validation errors.`);
      } else {
        throw new Error('No valid location points found in CSV file');
      }
    }
    
    // Return results
    return {
      locations: locations,
      validationErrors: validationErrors,
      skippedEntries: skippedEntries,
      totalProcessed: lines.length - startIndex,
      successfullyImported: locations.length
    };

  } catch (error) {
    console.error('Error importing CSV file:', error);
    throw error;
  }
}; 